import { GameState } from './GameState';
import { GameData } from '../data/GameData';
import { GameConfig } from '../core/GameConfig';
import { EventBus, GameEvents } from '../core/EventBus';
import { normalNpcSystem } from './NormalNpcSystem';
import { PERSONALITY_TEMPLATES, PersonalityKey } from '../data/tables/NormalNpcData';
import { ENCOUNTER_TEMPLATES, EncounterTemplate, EncounterScene, FIRST_MEET_CHOICES, MET_CHOICES, UNREACHABLE_LINES } from '../data/tables/EncounterTexts';
import { GIFTS, GIFT_MAP, GiftDef } from '../data/tables/Gifts';
import { PlotSystem } from './PlotSystem';
import { rankSystem } from './RankSystem';
import { fillTokensLoud, playerTokens } from '../core/TextTokens';
import { socialSystem } from './SocialSystem';
import { SpecialNpcDef } from '../data/types';

/**
 * 通告偶遇系统（docs/玩法设计.md §八 + 实现说明迁存章 A3）
 * 取代旧「合同首拍周自动结交全员」：拍摄周按咖位掷概率，命中后在偶遇池
 * （同剧组 castNpcIds + 剧本 relatedNPCs）中等概率抽 1 人：
 *  - 未结识 → 初遇 VN：单选项「认识一下」恒结识（置 0 入人脉页）——「冷漠」选项已删（2026-10-12）
 *  - 已结识 → 熟遇 VN（2026-10-10 改版）：开口后四选一，选完即收场
 *      闲聊 +4 ／ 送礼（扣背包一件，好感按 `Items.ts`）／ 邀约（好感到「熟悉」档才出这一格，
 *      成 +5 败 +1，⛔ 占人脉页每月 1 次邀约额度）／ 离开（0 好感、⛔ NPC 反应句）
 *      不成立的格子直接不显示（VnDialog 的选项没有置灰态，10-10 用户定「暂定不显示」）
 *    同一 NPC 每月好感增益限 1 次（用尽则本周不入池）——**离开也盖这个章**（用户定：
 *    否则一部戏十二个拍摄周里，同一个人会反复拿同一句开口来找你，每次都能被走开）
 * 概率 P = ENCOUNTER_BASE_PCT + (咖位-1) × ENCOUNTER_RANK_STEP_PCT；
 * 2026-10-16 茄子令：每剧首拍周必遇保底整条撤销（旧 2026-10-02 甲案的「免掷必抽 1 人」连同
 * `contract.encounterGranted` 旗标与 `claim` 盖章链一起删），每个拍摄周都按上面这条概率掷。
 * 同批加一道前置门：掷概率之前先查这部剧还有没有「未结识且能演初遇」的特殊 NPC，有就免掷概率、本周必演他
 * （判据与抽人规则见 roll 头注，取代旧 2026-10-10 的「优先锁池」）。
 * 撤保底的代价已当面算过并登记（docs/实现说明.md §3.4ax）：Lv.1 整部戏零结识 12.3%、
 * 1~2 周短位 39.3%；公园周末必遇 1 人与人脉页聊天/邀约是另外的口，不当作保底替身。
 * 2026-10-12 场景拆分：台词按 set（片场拍摄周）/ life（公园等自由出行）取不同池；探班独立池（visitLinesFor）。
 */

/** 熟遇动作类型（决定 resolve 走哪条结算；初遇⛔ 带此字段） */
export type MetAction = 'chat' | 'gift' | 'invite' | 'leave';

/** 偶遇选项（文案 + 效果；first 型用 meet 决定结不结识，met 型用 action 分流结算） */
export interface EncounterChoice {
    text: string;
    /** 初遇：是否结识入页（2026-10-12 起初遇收敛为单选项「认识一下」，恒 true；保留字段待扩展） */
    meet?: boolean;
    /** 好感增益（邀约那一格的成败在结算时才掷，故出格时给 0、数值与反应句都由 resolve 现出） */
    gain?: number;
    /** 选后 NPC 反应（VN 原位连播）；熟遇「离开」刻意留空 = 不回应 */
    reply: string;
    /** 初遇结识的旁白提示行（接在反应句后播：「〔XX 已加入人脉〕」，2026-10-12） */
    replyAfter?: string;
    /** 熟遇：动作类型 */
    action?: MetAction;
    /** action=gift：要扣的背包物品 id */
    itemId?: string;
    /** 邀约格右下的成功率徽章（与随机事件同一形制，⛔ 另立新样式） */
    prob?: number;
    /** VN 句下变动行（好感/物品当场可见）；邀约那格的在 resolve 里现出 */
    changes?: { label: string; delta: string }[];
}

/** 一次偶遇/探班的完整演出数据（roll 产出，UI 播 VN，玩家选定后回 resolve 结算） */
export interface EncounterOffer {
    kind: 'first' | 'met';
    /** 偶遇场景（2026-10-12 场景拆分）：set=片场拍摄周 / life=公园等自由出行 / visit=探班（2026-10-15 新增）——
     *  决定取哪套台词与结算日志措辞。探班走 buildVisitOffer → resolveVisit，⛔ 走 roll/resolve */
    scene: 'set' | 'life' | 'visit';
    /** 这张演出的来源：拍摄周＝合同 id，公园＝'park'，探班＝'visit'。
     *  旧用途是「演出交付方拿它去 claim 对应合同」（2026-10-10 甲案的盖章口），
     *  2026-10-16 保底撤销、claim 链一起删后，本字段只留作来源标识（日志与调试读数用），没有别的消费者 */
    contractId: string;
    npcId: string;
    npcName: string;
    /** 性格模板 emoji（特殊 NPC 固定 🤝） */
    emoji: string;
    /** 性格中文名（特殊 NPC 显示身份 role） */
    label: string;
    /** NPC 开口台词（逐句播） */
    lines: string[];
    choices: EncounterChoice[];
    /** 咖位差「够不着」（2026-10-12）：NPC 咖位高于玩家 ≥ ENCOUNTER_UNREACHABLE_RANK_GAP 时，
     *  纯旁白演出（无选项、不结识、不盖月戳）；UI 用旁白态 speaker。
     *  2026-10-16 撤保底后这一档的口径：那一次抽中机会就白花，下周重新按概率掷（用户裁定维持现状） */
    unreachable?: boolean;
}

/** resolve 后的结算摘要（月度弹窗结果块记账；反应句一般由 VN 选项 reply 连播，
 *  唯有熟遇「邀约」的成败要到结算时才掷，故它的反应句与变动行在这里回传） */
export interface EncounterOutcome {
    kind: 'first' | 'met';
    npcName: string;
    /** 初遇：是否结识；熟遇恒 true */
    met: boolean;
    gain: number;
    /** 一句话结果（「已加入人脉页」等） */
    note: string;
    /** 结算时才成立的 NPC 反应句（熟遇邀约；空串=没词，UI 不收尾连播） */
    reply?: string;
    /** 与 reply 同框的变动行 */
    changes?: { label: string; delta: string }[];
}

/**
 * 掷点结果（2026-10-10 甲案）：roll 不写任何状态，只报告「本周该播什么、或者为什么没戏」。
 * 旧签名 `EncounterOffer | null` 的那个 null 同时压着四种含义（查不到数据／没命中概率／池抽空／
 * 剧情事件接管），调用方分不清，演出是否真交付也就无从判断——10-06 到 10-10 期间 UI 一道错守卫
 * 能把首拍周保底静默吞掉四个月，土壤就在这。这次拆成带原因的三分，保底本身已于 2026-10-16 撤销，
 * 但「掷点纯查询、演出交给 UI」这条形状留着：剧情初遇那条路仍然靠它把派发权交回调度层。
 *  - `none`：本周没偶遇，`reason` 里 contractMissing／scriptMissing 属数据错误态（console 里另有条）；
 *  - `special`：这部剧还有未结识的特殊 NPC 且其初遇事件可派发——不由本系统派发，交回调度层（GameLoop）
 *    调 PlotSystem；剧情事件是「入队即安全」的形状，派发失败就本周不演（没有账要退，因为保底已撤）。
 *  - `offer`：本周该播偶遇 VN，由真把它播到玩家眼前的 UI 播（`resolve` 在玩家选定后才结算）。
 */
export type EncounterPlan =
    | { kind: 'none'; reason: 'contractMissing' | 'scriptMissing' | 'probMiss' | 'poolEmpty'; npcId?: string }
    | { kind: 'special'; npcId: string; eventId: string }
    | { kind: 'offer'; offer: EncounterOffer };

/**
 * 公园/咖啡馆偶遇掷点结果（自由出行批 2026-10-11；2026-10-28 茄子令改版：公园必遇改 50% 门、
 * 咖啡馆「随便坐坐」掷中走同一套池）：
 *  - `rest`：掷中休息档（公园，PARK_MEET_PCT 未中）——散步休息+体力，偶遇别抱太大指望；
 *  - `special`：抽中未结识特殊 NPC 且其初遇候选有「显式挂到公园（trigger.location='loc_park'）」的可派发者
 *    ——由调用方交 PlotSystem.fireByEvent 派发（剧情事件入收件箱即安全，公园侧本来就没有合同账本）；
 *  - `offer`：偶遇 VN（未结识普通 NPC=初遇可结识 / 已结识=熟遇四格，共用 encounterMetMonth 月戳）；
 *  - `stroll`：池抽空（全结识且本月月戳全用尽的极端态）——UI 落到各自的休息/独坐档兜底。
 */
export type ParkPlan =
    | { kind: 'rest' }
    | { kind: 'special'; npcId: string; eventId: string }
    | { kind: 'offer'; offer: EncounterOffer }
    | { kind: 'stroll' };

function pick<T>(arr: T[]): T {
    return arr[Math.floor(Math.random() * arr.length)];
}

export class EncounterSystem {
    private static instance: EncounterSystem;

    private constructor() { }

    static getInstance(): EncounterSystem {
        if (!this.instance) {
            this.instance = new EncounterSystem();
        }
        return this.instance;
    }

    /**
     * 拍摄周掷一次偶遇——不写任何状态（2026-10-10 甲案，见 EncounterPlan 的类型注释），只出 EncounterPlan。
     * 偶遇池 = 剧本 relatedNPCs + 合同 castNpcIds（同剧组）；已结识但本月熟遇增益已用尽的 NPC 不入池。
     *
     * **前置门（2026-10-16 茄子令）**：掷概率之前先查这部剧里还有没有「未结识且能演初遇」的特殊 NPC——
     * 有就免掷概率、本周必演他（多位则等概率抽 1）；全部结识完才回到正常掷点抽人。
     * 代价按同一周只有一个偶遇槽这条算：特殊未结识期间，那一周的槽被剧情初遇/轻量初遇占走，
     * 同组普通 NPC 那几周抽不到人。
     * 能演的判据（`pendingSpecialMeets`）：①手里有可派发的剧情初遇事件（见 `meetEventFor`，许林辉走这条，
     * 交回调度层派发）；②NPCDef 的 firstMeet + firstWarm 两池都有词（轻量初遇卡，罗一鸣 10-15 已写好这两池）；
     * ③事件已排队但还没演完＝本周不占门（flag 与结识在 deliver 那一刻才落，见 `meetEventInFlight`）。
     * ①②皆无、③也不成立＝配错/事件未发：他不该卡住整条偶遇线，所以不算门（用户裁定），
     * 同时把他从普通池里筛掉——留着只会被抽中然后播一张空卡。warn 照响，错配由读表哨兵拦。
     *
     * **特殊 NPC 的结识口只有两条**（2026-10-16 茄子定）：他自己的剧情初遇事件，或「有他在的剧组」的拍摄周
     * （＝本方法）。公园那条通道不结识未结识的特殊 NPC，口径见 `rollPark` 头注。
     *
     * 概率尺：每个拍摄周都按 P = ENCOUNTER_BASE_PCT + (咖位-1) × ENCOUNTER_RANK_STEP_PCT 独立掷，没有免掷周——
     * 2026-10-16 茄子令撤销「每剧首拍周必遇保底」（旧 2026-10-02 甲案），合同上的 encounterGranted 旗标
     * 与 claim 盖章链一并删除。代价已量化登记（docs/实现说明.md §3.4ax）。
     * 同周有随机事件不再让位（同日茄子令）：两条通道各自独立掷点，同一周可以既有事件又有偶遇，
     * 由月度执行页串行播（事件读毕、剧情读毕，才轮到偶遇）。旧写法是事件占周时 roll 根本不被调用。
     * 随机 NPC 之间仍不分已结识/未结识优先级（2026-09-25 拍板只管随机 NPC，未被本条覆盖）。
     */
    roll(contractId: string): EncounterPlan {
        const state = GameState.getInstance();
        const p = state.player;
        const contract = p.contracts.find(c => c.id === contractId);
        if (!contract) {
            console.warn(`[Encounter] 掷偶遇的合同 ${contractId} 查无，本周无偶遇（数据错误态）`);
            return { kind: 'none', reason: 'contractMissing' };
        }
        const script = GameData.SCRIPT_MAP[contract.scriptId];
        if (!script) {
            console.warn(`[Encounter] 合同 ${contractId} 的剧本 ${contract.scriptId} 查无，本周无偶遇（数据错误态）`);
            return { kind: 'none', reason: 'scriptMissing' };
        }

        const pool = [...script.relatedNPCs, ...(contract.castNpcIds ?? [])];

        // 前置门：这部剧还有能演初遇的特殊 NPC，免掷概率，本周就是他的
        const pending = this.pendingSpecialMeets(pool);
        if (pending.length > 0) {
            const who = pick(pending);
            if (who.eventId) return { kind: 'special', npcId: who.npcId, eventId: who.eventId };
            return { kind: 'offer', offer: this.buildFirstOffer(contractId, who.npcId, 'set') };
        }

        const prob = GameConfig.ENCOUNTER_BASE_PCT + (p.rank - 1) * GameConfig.ENCOUNTER_RANK_STEP_PCT;
        if (Math.random() * 100 >= prob) return { kind: 'none', reason: 'probMiss' };

        const stamp = p.encounterMetMonth ?? {};
        const absMonth = p.year * 12 + p.month;
        const candidates = pool.filter(id => {
            // 未结识的特殊 NPC 走到这儿必然演不了（能演的已被前置门接走），筛掉免得抽中播空卡
            if (state.getFavor(id) < 0 && this.isSpecialNpc(id)) return false;
            const favor = state.getFavor(id);
            if (favor < 0) return true;                       // 未结识的随机 NPC：永远可初遇
            return (stamp[id] ?? 0) !== absMonth;             // 已结识：本月熟遇增益未用尽才可入池
        });
        if (candidates.length === 0) return { kind: 'none', reason: 'poolEmpty' };   // 池被筛空：这周没有偶遇，下周重新掷

        const picked = pick(candidates);
        return {
            kind: 'offer',
            offer: state.getFavor(picked) < 0
                ? this.buildFirstOffer(contractId, picked, 'set')
                : this.buildMetOffer(contractId, picked, 'set'),
        };
    }

    /** 附表在册的特殊 NPC（含从派角池进组的竞争型实例——她们也活在 player.normalNpcs 里） */
    private isSpecialNpc(npcId: string): boolean {
        return GameData.SPECIAL_NPCS.some(s => s.npcId === npcId);
    }

    /** 这位特殊 NPC 有没有手写的轻量初遇词（开口句 + 反应句两池都得有，缺一就播成半张空卡）。
     *  只认 NPCDef 上的真池：`templateFor` 对查无此人的 id 会回落到随机 NPC 模板，
     *  拿它做判据会把「附表里有、主表里还没落人」的配置错算成「能演」，播出是一张名字为 id 的空卡。 */
    private hasLightweightMeet(npcId: string): boolean {
        const def = GameData.NPC_MAP[npcId];
        return !!def && (def.firstMeet?.length ?? 0) > 0 && (def.firstWarm?.length ?? 0) > 0;
    }

    /**
     * 前置门的候选：池子里「附表在册 + 未结识 + 初遇演得出」的特殊 NPC。
     * 演得出的两档（ eventId 非空＝剧情初遇，null＝轻量初遇卡）见 roll 的头注。
     * 多位时由调用方等概率抽 1（沿用旧锁池那把尺）；已结识者不在这里出现（走正常池熟遇）。
     */
    private pendingSpecialMeets(pool: string[]): { npcId: string; eventId: string | null }[] {
        const state = GameState.getInstance();
        const out: { npcId: string; eventId: string | null }[] = [];
        for (const npcId of new Set(pool)) {
            if (!this.isSpecialNpc(npcId)) continue;
            if (state.getFavor(npcId) >= 0) continue;                 // 已结识：熟遇那条正常路
            if (this.meetEventInFlight(npcId)) continue;              // 剧情已在队，演完自然结识，别再占门
            const eventId = this.meetEventFor(npcId);
            if (eventId) { out.push({ npcId, eventId }); continue; }
            if (this.hasLightweightMeet(npcId)) { out.push({ npcId, eventId: null }); continue; }
            console.warn(`[Encounter] ${npcId} 未结识，但初遇既无可派发事件、也没写 firstMeet/firstWarm，本周不演他，也不拦正常掷点（配错态，读表哨兵该拦）`);
        }
        return out;
    }

    /** 这位特殊 NPC 的初遇事件是否「已排队、还没演完」。
     *  flag 与结识都要到 deliver 那一刻才落（10-13 收口的形状），期间 `meetEventFor` 查不出「触发过」，
     *  前置门会每周重新捞同一条、而 `fire` 因「已在队未演完」恒退货，就白占一个偶遇槽。
     *  尺现成：`PlotSystem.meetEventAtLocation` 用的是同一个 pendingMessages 集合。 */
    private meetEventInFlight(npcId: string): boolean {
        const sp = GameData.SPECIAL_NPCS.find(s => s.npcId === npcId);
        if (!sp) return false;
        const queued = new Set(PlotSystem.getInstance().pendingMessages().map(m => m.eventId));
        return sp.meet.events.some(id => queued.has(id));
    }

    /**
     * 公园偶遇掷点（自由出行批 2026-10-11；2026-10-28 茄子令「公园也改成 50% 概率遇到 NPC」）：
     * 先掷 PARK_MEET_PCT 门（未中=rest 散步休息档），门过后按池抽——池 = 普通 NPC 15 人全池
     * （未结识可初遇结识/已结识熟遇）+ 特殊 NPC（已结识=熟遇；未结识走「显式挂到公园」的初遇剧情事件
     * 候选，按候选序取第一个满足者）。已结识且本月熟遇月戳已用尽者不入池（与拍摄周同规）；
     * 池抽空（全结识+全用尽的极端态）= stroll，UI 落到散步休息档兜底。
     * 2026-10-16 茄子定口径：**特殊 NPC 的结识口只有两条——他自己的剧情初遇事件，或有他在的剧组拍摄周。**
     * 所以公园不给未结识的特殊 NPC 出轻量初遇卡（firstMeet/firstWarm 只喂拍摄周那道前置门），
     * 这里查不到挂公园的事件就干脆不入池——不是兜底失效，是设计里没有这条路。
     * 演出/结算全链复用 buildFirstOffer/buildMetOffer/resolve（offer.contractId='park' 只作来源标识，
     * 公园没有合同账本，月戳才是闸门），另起第二套文案体系。
     */
    rollPark(): ParkPlan {
        if (Math.random() * 100 >= GameConfig.PARK_MEET_PCT) return { kind: 'rest' };
        return this.rollSocialEncounter(true);
    }

    /**
     * 咖啡馆偶遇掷点（2026-10-28 茄子令「随便坐坐 50% 偶遇，和公园一样」）：池与公园完全同款
     * （含未结识普通 NPC——咖啡厅随本批成为第三结识入口，铁律修订见 docs/玩法设计.md），
     * 唯一例外＝**未结识特殊 NPC 不入池**：他们的初遇是剧情事件、绑死触发地点（公园的归公园，
     * 咖啡馆的在进店那一步就由 meetEventAtLocation 优先消费），掷点池里再出就是场景穿帮。
     * 已结识特殊 NPC 的熟遇格照常入池（月戳与公园共一本账）。
     */
    rollCafe(): ParkPlan {
        return this.rollSocialEncounter(false);
    }

    /** 公园/咖啡馆共用的偶遇池抽取（`includeUnmetSpecials`=是否给未结识特殊 NPC 的公园挂事件开口） */
    private rollSocialEncounter(includeUnmetSpecials: boolean): ParkPlan {
        const state = GameState.getInstance();
        const p = state.player;
        const absMonth = p.year * 12 + p.month;
        const stamp = p.encounterMetMonth ?? {};
        const pool: { npcId: string; eventId?: string }[] = [];
        // 特殊 NPC：未结识=可派发的初遇候选；已结识=熟遇格（月戳未用才入池）
        for (const sp of GameData.SPECIAL_NPCS) {
            if (state.getFavor(sp.npcId) >= 0) {
                if ((stamp[sp.npcId] ?? 0) !== absMonth) pool.push({ npcId: sp.npcId });
                continue;
            }
            if (!includeUnmetSpecials) continue;
            const ev = this.parkMeetEvent(sp);
            if (ev) pool.push({ npcId: sp.npcId, eventId: ev });
        }
        // 普通 NPC 全池：⛔ 特殊 NPC（竞争型实例）已在上方 SPECIAL_NPCS 循环入池，跳过免得同一人入池两次；
        // 熟遇月戳已用尽的跳过（未结识恒可入池）
        for (const n of p.normalNpcs) {
            if (GameData.NPC_MAP[n.id]) continue;
            if (state.getFavor(n.id) >= 0 && (stamp[n.id] ?? 0) === absMonth) continue;
            pool.push({ npcId: n.id });
        }
        if (pool.length === 0) return { kind: 'stroll' };
        const picked = pick(pool);
        if (picked.eventId) return { kind: 'special', npcId: picked.npcId, eventId: picked.eventId };
        return {
            kind: 'offer',
            offer: state.getFavor(picked.npcId) < 0
                ? this.buildFirstOffer('park', picked.npcId, 'life')
                : this.buildMetOffer('park', picked.npcId, 'life'),
        };
    }

    /**
     * 特殊 NPC 的公园初遇候选（候选序取第一个满足者）：未触发、事件存在、主 NPC 判据不穿帮、
     * trigger 显式挂公园（location='loc_park'）且时间窗满足。无 location 的候选⛔ 在公园派发——
     * 那是月度扫描时间线的货（错过有别的渠道，设计 §四 联动登记的原话）。
     */
    private parkMeetEvent(sp: SpecialNpcDef): string | null {
        const state = GameState.getInstance();
        const plot = PlotSystem.getInstance();
        for (const id of sp.meet.events) {
            if (state.player.flags[id]) continue;
            const ev = plot.eventById(id);
            if (!ev?.trigger) continue;
            if (ev.trigger.location !== 'loc_park') continue;
            const lead = ev.npcId ?? ev.meetNpcs?.[0];
            if (lead && lead !== sp.npcId && state.getFavor(lead) >= 0) continue;
            if (!plot.isTriggerMet(ev.trigger)) continue;
            return id;
        }
        return null;
    }

    /**
     * 这位特殊 NPC 手里「现在能演」的剧情初遇事件 id；没有则 null。
     * 2026-10-16 茄子令去掉旧的「必须绑在剧本 relatedNPCs 上」这道门槛（判据挪进
     * `pendingSpecialMeets`，那边同时管特殊在册 + 未结识），因为从派角池进组的同组特殊 NPC
     * 不在 relatedNPCs 里、旧写法永远查不到事件。普通 NPC 由调用方挡在门外，这里查不到附表即 null。
     * 三种配错各打一条 console.warn（兜底要响＝保护玩家 + 开发期喊话，配置错误本身由
     * `tests/_check_special_npcs.mjs` 拦在上线前）：
     *  ①事件 id 在 FIXED_EVENTS 查无（拼错／事件还没导码）；
     *  ②事件主 NPC（npcId ?? meetNpcs[0]）填成了别人且那位已结识——fire 的 lead 判据会整场退货，
     *    「熟人引荐」这类写法必须换成该 NPC 自己的事件，否则永远不演；
     *  ③候选全部已触发过——最常见是事件里漏写 meetNpcs（flag 置了人却没结识，于是永久卡在这条路上）。
     *  `meet.events` 本为空不在此列：那是 2026-10-15 起的合法形态（罗一鸣＝相识锚点事件待发，
     *  先靠主表的 firstMeet/firstWarm 演轻量初遇），要不要喊话由 `pendingSpecialMeets` 统一判。
     */
    private meetEventFor(npcId: string): string | null {
        const state = GameState.getInstance();
        const sp: SpecialNpcDef | undefined = GameData.SPECIAL_NPCS.find(s => s.npcId === npcId);
        if (!sp) return null;                                     // 附表里没有这个 id＝数据错，交读表桩
        const plot = PlotSystem.getInstance();
        for (const id of sp.meet.events) {
            if (state.player.flags[id]) continue;                 // 触发过：下一条候选（不同时期初遇剧情不同）
            const ev = plot.eventById(id);
            if (!ev) {
                console.warn(`[Encounter] ${npcId} 的初遇候选 ${id} 在 FIXED_EVENTS 查无 ⇒ 跳过该候选`);
                continue;
            }
            const lead = ev.npcId ?? ev.meetNpcs?.[0];
            if (lead && lead !== npcId && state.getFavor(lead) >= 0) {
                console.warn(`[Encounter] ${npcId} 的初遇候选 ${id} 主 NPC 填成已结识的 ${lead} ⇒ fire 会整场退货，跳过该候选`);
                continue;
            }
            return id;
        }
        if (sp.meet.events.length > 0 && sp.meet.events.every(id => state.player.flags[id])) {
            console.warn(`[Encounter] ${npcId} 仍未结识但初遇候选已全部触发，多半是事件漏写 meetNpcs，演完没把人加进人脉页`);
        }
        return null;
    }

    /** 玩家选定选项 → 结算好感/结识，返回摘要供 UI 结果块展示 */
    resolve(offer: EncounterOffer, choiceIndex: number): EncounterOutcome {
        const state = GameState.getInstance();
        const p = state.player;
        const choice = offer.choices[choiceIndex] ?? offer.choices[0];
        const where = offer.scene === 'set' ? '片场偶遇' : '偶遇';

        if (offer.kind === 'first') {
            state.meetNpc(offer.npcId);
            return { kind: 'first', npcName: offer.npcName, met: true, gain: 0, note: `结识 ${offer.npcName}，已加入人脉页` };
        }

        // 熟遇：任何一格选完都盖本月章（含「离开」——否则同一句开口反复骚扰）
        const stampMonth = () => { p.encounterMetMonth = { ...(p.encounterMetMonth ?? {}), [offer.npcId]: p.year * 12 + p.month }; };

        // 送礼结算保留（buildMetOffer 不再出这一格——2026-10-15 暂不实现；旧档/测试可能仍带 gift choice）
        if (choice.action === 'gift') {
            const item = choice.itemId ? GIFT_MAP[choice.itemId] : undefined;
            if (!item || !state.takeItem(item.id)) {
                stampMonth();
                return { kind: 'met', npcName: offer.npcName, met: true, gain: 0, note: `想送 ${offer.npcName} 点什么，包里已经空了` };
            }
            const gain = socialSystem.giftFavorFor(offer.npcId, item);
            state.addFavor(offer.npcId, gain);
            stampMonth();
            return { kind: 'met', npcName: offer.npcName, met: true, gain, note: `送 ${offer.npcName} 一份${item.name}，好感 +${gain}` };
        }

        const outcome = this.resolveMetChoice(offer, choice, where);
        stampMonth();
        return outcome;
    }

    /** 探班开口句：读独立探班池 visit.meet（2026-10-12 场景拆分，⛔ 复用熟遇池——
     *  「特地探望」与「同环境偶遇」语义不同，用户定）；闲聊 reply 走 metChat（2026-10-15 删 visit.chat 后统一）。
     *  特殊 NPC 仍读其手写 visitMeet（见 templateFor）。2026-10-15 探班重构后本方法保留供旧路径兼容。 */
    visitLinesFor(npcId: string): string[] {
        const tpl = this.templateFor(npcId);
        if (tpl.visit.meet.length > 0) return [this.txt(pick(tpl.visit.meet), '探班开口句')];
        return ['「来啦，正好歇会儿。」'];
    }

    /**
     * 构建探班演出数据（2026-10-15）：开口句（visit.meet）+ 选项（闲聊/邀约/离开）。
     * 选项构建委托 buildMetChoices，差异由参数注入：
     *  - chatGain=VISIT_GAIN，chatReply 从 set.metChat 取（探班固定片场场景）
     *  - scene='visit'，contractId='visit'（无真实合同，本字段只作来源标识）
     *  - 送礼暂不出（2026-10-15 茄子令）
     */
    buildVisitOffer(npcId: string): EncounterOffer {
        const info = this.infoFor(npcId);
        const tpl = this.templateFor(npcId);
        const chatReply = tpl.set.metChat.length > 0 ? this.txt(pick(tpl.set.metChat), '探班闲聊反应句') : '「谢谢你来看我。」';
        return {
            kind: 'met',
            scene: 'visit',
            contractId: 'visit',
            npcId,
            npcName: info.name,
            emoji: info.emoji,
            label: info.label,
            lines: [tpl.visit.meet.length > 0 ? this.txt(pick(tpl.visit.meet), '探班开口句') : '「来啦，正好歇会儿。」'],
            choices: this.buildMetChoices(npcId, GameConfig.VISIT_GAIN, chatReply),
        };
    }

    /**
     * 探班选项结算（2026-10-15）：委托 resolveMetChoice，⛔ 盖 encounterMetMonth（visitMonth 已在 visit() 盖过）。
     */
    resolveVisit(offer: EncounterOffer, choiceIndex: number): EncounterOutcome {
        const choice = offer.choices[choiceIndex] ?? offer.choices[0];
        return this.resolveMetChoice(offer, choice, '探班');
    }

    /**
     * 熟遇/探班公共选项构建（2026-10-15 抽取）：闲聊 + 邀约（好感≥40 才出）+ 离开。
     * 送礼暂不实现（2026-10-15 茄子令）。差异由参数注入：
     *  - chatGain：闲聊好感（熟遇 ENCOUNTER_GAIN_CHAT / 探班 VISIT_GAIN）
     *  - chatReply：闲聊 NPC 反应句（熟遇走场景 metChat / 探班走 set.metChat）
     */
    private buildMetChoices(npcId: string, chatGain: number, chatReply: string): EncounterChoice[] {
        const state = GameState.getInstance();
        const choices: EncounterChoice[] = [{
            text: MET_CHOICES.chat,
            action: 'chat',
            gain: chatGain,
            reply: chatReply,
            changes: [{ label: '好感', delta: `+${chatGain}` }],
        }];

        if (socialSystem.getFavorLevel(npcId).level >= 3) {
            const favor = state.getFavor(npcId);
            choices.push({
                text: MET_CHOICES.invite,
                action: 'invite',
                gain: 0,
                reply: '',
                prob: Math.min(GameConfig.ENCOUNTER_INVITE_PROB_CAP, favor),
            });
        }

        choices.push({ text: MET_CHOICES.leave, action: 'leave', gain: 0, reply: '' });
        return choices;
    }

    /**
     * 熟遇/探班公共结算（2026-10-15 抽取）：闲聊/邀约/离开三条分支。
     * ⛔ 盖月戳（由调用方处理：熟遇盖 encounterMetMonth，探班不盖）。
     * `where` = 日志/note 措辞前缀（"片场偶遇"/"偶遇"/"探班"）。
     */
    private resolveMetChoice(offer: EncounterOffer, choice: EncounterChoice, where: string): EncounterOutcome {
        const state = GameState.getInstance();

        if (choice.action === 'leave') {
            return { kind: 'met', npcName: offer.npcName, met: true, gain: 0, note: `与 ${offer.npcName} 点头错过` };
        }

        if (choice.action === 'invite') {
            const favor = state.getFavor(offer.npcId);
            const success = Math.random() * 100 < Math.min(GameConfig.ENCOUNTER_INVITE_PROB_CAP, favor);
            const gain = success ? GameConfig.ENCOUNTER_INVITE_GAIN_OK : GameConfig.ENCOUNTER_INVITE_GAIN_FAIL;
            const texts = socialSystem.getNpcTexts(offer.npcId);
            state.addFavor(offer.npcId, gain);
            return {
                kind: 'met', npcName: offer.npcName, met: true, gain,
                note: success ? `约到 ${offer.npcName} 一顿饭，好感 +${gain}` : `${offer.npcName} 这顿没约成，好感 +${gain}`,
                reply: this.txt(success ? (texts?.inviteOkText ?? '') : (texts?.inviteFailText ?? ''), '邀约反应句'),
                changes: [{ label: '好感', delta: `+${gain}` }],
            };
        }

        // 闲聊
        const gain = choice.gain ?? 0;
        state.addFavor(offer.npcId, gain);
        return { kind: 'met', npcName: offer.npcName, met: true, gain, note: `${where} ${offer.npcName}，好感 +${gain}` };
    }

    /**
     * 台词出口的唯一插值口（2026-10-16 茄子令「收成一把尺」）：文案表里写的 `%玩家名%` 一这类记号，
     * 过去只有评价库认，偶遇/探班这条链一枚都不替，于是罗一鸣那句初遇把 `%玩家名%` 原样播上了屏。
     * 现在所有从文案池取出的句子都过 `core/TextTokens`，与评价库同吃一份清单与同一把正则。
     * 取不到值时**原样留句 + console.warn**（台词整条撤就是一张空卡，与评价库「整条跳过」是两种口径，
     * 这点在 TextTokens 头注写明）；未登记的记号由读表哨兵 `tests/_check_text_tokens.mjs` 拦在上线前。
     */
    private txt(text: string, where: string): string {
        const p = GameState.getInstance().player;
        return fillTokensLoud(text, playerTokens({ name: p.name, rank: p.rank, fans: p.fans },
            rankSystem.getRankName(p.rank)), where);
    }

    /** 按 NPC 性格模板取偶遇文案；特殊 NPC（无性格）走 special 池 */
    private templateFor(npcId: string): EncounterTemplate {
        // 特殊 NPC：NPCDef 专属文案池（2026-10-06 茄子定：每人手写，⛔ 复用随机 NPC 模板、无共享兜底）；
        // 2026-10-12 场景拆分：手写池无场景差分，set/life 共用同一套。
        // 2026-10-15 探班独立：visit.meet 读 visitMeet（探班专属开口句），闲聊 reply 走 set.metChat（=encMetChat）。
        // 2026-10-15 初遇字段：firstMeet/firstWarm 从 NPCDef 读入（有 meet.events 时优先走剧情事件，此字段兜底）。
        const specialDef = GameData.NPC_MAP[npcId];
        if (specialDef) {
            const shared: EncounterScene = {
                firstMeet: specialDef.firstMeet ?? [],
                metMeet: specialDef.encMetMeet,
                metChat: specialDef.encMetChat,
            };
            return {
                firstWarm: specialDef.firstWarm ?? [],
                set: shared, life: shared,
                visit: { meet: specialDef.visitMeet?.length ? specialDef.visitMeet : specialDef.encMetMeet },
            };
        }
        const normal = normalNpcSystem.findNpc(npcId);
        if (normal) {
            return ENCOUNTER_TEMPLATES[normal.personality as PersonalityKey] ?? ENCOUNTER_TEMPLATES.special;
        }
        return ENCOUNTER_TEMPLATES.special;   // 随机 NPC 无 personality 匹配时的最后兜底（特殊 NPC ⛔ 走到这）
    }

    /** NPC 展示信息（名字/emoji/副标签）；副标签只给特殊 NPC 显身份 role——
     *  普通 NPC ⛔ 不显性格名（用户拍板：整个游戏不写明性格，只用对话体现），emoji 仅作品类目视区分 */
    private infoFor(npcId: string): { name: string; emoji: string; label: string } {
        const special = GameData.NPC_MAP[npcId];
        if (special) return { name: special.name, emoji: '🤝', label: special.role };
        const normal = normalNpcSystem.findNpc(npcId);
        if (normal) {
            const t = PERSONALITY_TEMPLATES[normal.personality as PersonalityKey];
            return { name: normal.name, emoji: t?.emoji ?? '🤝', label: '' };
        }
        return { name: npcId, emoji: '🤝', label: '' };
    }

    private buildFirstOffer(contractId: string, npcId: string, scene: 'set' | 'life'): EncounterOffer {
        const info = this.infoFor(npcId);
        const tpl = this.templateFor(npcId);
        const sc = tpl[scene];
        const state = GameState.getInstance();
        const npcRank = this.npcRankOf(npcId);
        // 咖位差「够不着」：普通 NPC 咖位比玩家高 ≥ 门槛时播纯旁白，不结识、不盖月戳（那一次抽中机会就白花）
        // 2026-10-16 补一道「不是特殊 NPC」：竞争型特殊 NPC（罗一鸣 rank 5）也活在派角池里，npcRankOf 查得到他，
        // 而前置门每周都要把他端出来——让他吃这道门就成了 Lv.1 的永久空转（旁白卡无选项、不结识、下周照演）。
        // 口径沿 2026-10-12 那条「特殊 NPC 不设门槛」：他是剧本绑定的人，卡住他等于卡住一条剧情线。
        if (!this.isSpecialNpc(npcId) && npcRank >= 0 && npcRank - state.player.rank >= GameConfig.ENCOUNTER_UNREACHABLE_RANK_GAP) {
            return {
                kind: 'first',
                scene,
                contractId,
                npcId,
                npcName: info.name,
                emoji: info.emoji,
                label: info.label,
                lines: [this.txt(UNREACHABLE_LINES.meet.replace('{name}', info.name), '够不着旁白')],
                choices: [],
                unreachable: true,
            };
        }
        return {
            kind: 'first',
            scene,
            contractId,
            npcId,
            npcName: info.name,
            emoji: info.emoji,
            label: info.label,
            lines: [this.txt(pick(sc.firstMeet), '初遇开口句')],
            choices: [
                // 变动行报 NPC 名（2026-10-13 茄子令：「人脉 已结识」的 label 换成名字——玩家要看的是认识了谁）
                { text: FIRST_MEET_CHOICES.meet, meet: true, reply: this.txt(pick(tpl.firstWarm), '初遇反应句'), changes: [{ label: info.name, delta: '已结识' }] },
            ],
        };
    }

    /** 普通 NPC 咖位（特殊 NPC 无 rank，返回 -1 表示不走够不着判定） */
    private npcRankOf(npcId: string): number {
        const normal = normalNpcSystem.findNpc(npcId);
        return normal ? normal.rank : -1;
    }

    /** 送礼格要送的那件：背包里第一件持有数 > 0 的礼物（按 `GIFT_ITEMS` 表序）。
     *  ⚠️ 本轮背包只有测试礼物一件；等礼物来源批做出一包多物，这里要换成选品交互。 */
    private giftInBag(): GiftDef | null {
        const state = GameState.getInstance();
        for (const item of GIFTS) {
            if (state.getItemCount(item.id) > 0) return item;
        }
        return null;
    }

    private buildMetOffer(contractId: string, npcId: string, scene: 'set' | 'life'): EncounterOffer {
        const info = this.infoFor(npcId);
        const tpl = this.templateFor(npcId);
        const sc = tpl[scene];
        // 选项委托 buildMetChoices；送礼暂不实现（2026-10-15 茄子令，与探班一致）
        return {
            kind: 'met',
            scene,
            contractId,
            npcId,
            npcName: info.name,
            emoji: info.emoji,
            label: info.label,
            lines: [this.txt(pick(sc.metMeet), '熟遇开口句')],
            choices: this.buildMetChoices(npcId, GameConfig.ENCOUNTER_GAIN_CHAT, this.txt(pick(sc.metChat), '熟遇闲聊反应句')),
        };
    }
}

/** 导出单例 */
export const encounterSystem = EncounterSystem.getInstance();
